import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { forkJoin } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { SiteService } from 'src/app/services/site.service';
import {
	CommActivityPointDto,
	CommCampaignDto,
	CommDashboardService,
	CommDashboardSummaryDto,
	CommEmployerDetailDto,
	CommEmployerDto,
	Period
} from '../comm-dashboard/comm-dashboard.service';

type ScheduleStatus = 'sent' | 'scheduled' | 'overdue' | 'paused';

interface CampaignRow {
	name: string;
	type: 'Single' | 'Drip';
	isScheduled: boolean;
	scheduleStatus: ScheduleStatus;
	emailSent: number;
	emailDeliveredPct: number | null;
	openedPct: number | null;
	clickedPct: number | null;
	bouncedPct: number | null;
	smsSent: number;
	smsDeliveredPct: number | null;
	smsFailPct: number | null;
	date: Date | null;
	flags: string[];
}

interface SystemAlert {
	type: 'warning' | 'info' | 'error';
	category: string;
	message: string;
}

interface ChartSeries {
	label: string;
	color: string;
	data: number[];
	points?: string;
	areaPoints?: string;
	dots?: { x: number; y: number }[];
}

interface ChartData {
	series: ChartSeries[];
	gridLines: { y: number; label: string }[];
	xLabels: { x: number; label: string }[];
}

type EmployerStage = 'build' | 'staged' | 'active';
type CommPreference = 'email' | 'sms' | 'both';

interface EmailStats {
	requests: number;
	deliveredPct: number;
	openedPct: number;
	clickedPct: number;
	bouncesPct: number;
	spamPct: number;
	unsubscribedPct: number;
}

interface SmsStats {
	sent: number;
	deliveredPct: number;
	undeliveredPct: number;
	failedPct: number;
}

interface LifecycleInfo {
	dripStartDate: Date | null;
	censusLoaded: number;
	commPreference: CommPreference;
	campaignsSent: number;
	campaignsTotal: number;
	lastCampaign: Date | null;
	nextCampaign: Date | null;
	stage: EmployerStage;
}

interface EmployerDetail {
	name: string;
	lifecycle: LifecycleInfo;
	allTimeEmail: EmailStats;
	allTimeSms: SmsStats;
	emailStats: EmailStats;
	smsStats: SmsStats;
	systemAlerts: SystemAlert[];
}

@Component({
	selector: 'app-comm-dashboard-detail',
	templateUrl: './comm-dashboard-detail.component.html',
	styleUrls: ['./comm-dashboard-detail.component.scss']
})
export class CommDashboardDetailComponent implements OnInit, OnDestroy {

	public employerDetail: EmployerDetail;
	public period: Period = 'week';

	public loading = true;
	public loadError = false;

	public emailChart: ChartData;
	public smsChart: ChartData;

	// Empty = no client-side sort applied; the grid shows the server's default order
	// (drip steps in program sequence, then one-time campaigns by date). Set when a header is clicked.
	public sortColumn = '';
	public sortDirection: 'asc' | 'desc' = 'asc';

	public campaigns: CampaignRow[] = [];

	private employerId: string;
	private activityPoints: CommActivityPointDto[] = [];

	private readonly weekdayAbbr = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
	private readonly monthAbbr = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

	constructor(
		private route: ActivatedRoute,
		private siteService: SiteService,
		private dashboard: CommDashboardService
	) {}

	ngOnInit() {
		this.employerId = this.route.snapshot.paramMap.get('employerId');
		this.siteService.backButton = { link: '/admin/campaigns/comm-dashboard', title: 'Back to Communication Dashboard' };
		this.siteService.headerButtons = [];
		this.loadAll();
	}

	ngOnDestroy() {
		this.siteService.backButton = null;
	}

	// ── Data loading ──────────────────────────────────────────────────

	reload() {
		this.loadAll();
	}

	private loadAll() {
		this.loading = true;
		this.loadError = false;
		forkJoin({
			detail: this.dashboard.getEmployerDetail(this.employerId),
			summary: this.dashboard.getSummary(this.period, this.employerId),
			activity: this.dashboard.getActivity(this.period, this.employerId)
		})
			.pipe(finalize(() => (this.loading = false)))
			.subscribe({
				next: ({ detail, summary, activity }) => {
					this.employerDetail = this.mapDetail(detail, summary);
					this.siteService.pageTitle = this.employerDetail.name;
					// Preserve the server's default order (program sequence, then one-time by date).
					this.campaigns = detail.campaigns.map(c => this.mapCampaign(c));
					this.activityPoints = activity;
					this.buildCharts();
				},
				error: () => (this.loadError = true)
			});
	}

	setPeriod(p: Period) {
		if (this.period === p) {
			return;
		}
		this.period = p;
		forkJoin({
			summary: this.dashboard.getSummary(this.period, this.employerId),
			activity: this.dashboard.getActivity(this.period, this.employerId)
		}).subscribe({
			next: ({ summary, activity }) => {
				if (this.employerDetail) {
					this.employerDetail.emailStats = this.toEmailStats(summary);
					this.employerDetail.smsStats = this.toSmsStats(summary);
				}
				this.activityPoints = activity;
				this.buildCharts();
			},
			error: () => (this.loadError = true)
		});
	}

	// ── Mapping ───────────────────────────────────────────────────────

	private mapDetail(detail: CommEmployerDetailDto, periodSummary: CommDashboardSummaryDto): EmployerDetail {
		const e = detail.employer;
		return {
			name: e.employerName,
			lifecycle: this.toLifecycle(e),
			allTimeEmail: this.toEmailStats(detail.allTime),
			allTimeSms: this.toSmsStats(detail.allTime),
			emailStats: this.toEmailStats(periodSummary),
			smsStats: this.toSmsStats(periodSummary),
			systemAlerts: detail.alerts.map(a => ({ type: a.type, category: a.category, message: a.message }))
		};
	}

	private toLifecycle(e: CommEmployerDto): LifecycleInfo {
		return {
			dripStartDate: e.dripStartDate ? new Date(e.dripStartDate) : null,
			censusLoaded: e.censusLoaded,
			commPreference: this.mapCommPref(e.deliveryTypeID),
			campaignsSent: e.campaignsSent,
			campaignsTotal: e.campaignsTotal,
			lastCampaign: e.lastCampaign ? new Date(e.lastCampaign) : null,
			nextCampaign: e.nextCampaign ? new Date(e.nextCampaign) : null,
			stage: e.stage
		};
	}

	private toEmailStats(s: CommDashboardSummaryDto): EmailStats {
		return {
			requests: s.emailRequests,
			deliveredPct: s.emailDeliveredPct,
			openedPct: s.emailOpenedPct,
			clickedPct: s.emailClickedPct,
			bouncesPct: s.emailBouncesPct,
			spamPct: s.emailSpamPct,
			unsubscribedPct: s.emailUnsubscribedPct
		};
	}

	private toSmsStats(s: CommDashboardSummaryDto): SmsStats {
		return {
			sent: s.smsSent,
			deliveredPct: s.smsDeliveredPct,
			undeliveredPct: s.smsUndeliveredPct,
			failedPct: s.smsFailedPct
		};
	}

	private mapCampaign(c: CommCampaignDto): CampaignRow {
		return {
			name: c.campaignName,
			type: c.type,
			isScheduled: c.isScheduled,
			scheduleStatus: c.scheduleStatus,
			emailSent: c.emailSent,
			emailDeliveredPct: c.emailDeliveredPct,
			openedPct: c.openedPct,
			clickedPct: c.clickedPct,
			bouncedPct: c.bouncedPct,
			smsSent: c.smsSent,
			smsDeliveredPct: c.smsDeliveredPct,
			smsFailPct: c.smsFailPct,
			date: c.sendDate ? new Date(c.sendDate) : null,
			flags: this.buildFlags(c)
		};
	}

	private buildFlags(c: CommCampaignDto): string[] {
		const flags: string[] = [];
		if (c.alertHighBounce) flags.push(`High bounce rate (${(c.bouncedPct ?? 0).toFixed(1)}%)`);
		if (c.alertLowDelivery) flags.push(`Low email delivery (${(c.emailDeliveredPct ?? 0).toFixed(1)}%)`);
		if (c.alertHighSpam) flags.push(`High spam rate (${(c.spamPct ?? 0).toFixed(2)}%)`);
		if (c.alertHighDrop) flags.push('High drop rate');
		if (c.alertHighDeferral) flags.push('Elevated deferral rate');
		if (c.alertHighSmsFailure) flags.push(`High SMS failure (${(c.smsFailPct ?? 0).toFixed(1)}%)`);
		if (c.alertLowSmsDelivery) flags.push(`Low SMS delivery (${(c.smsDeliveredPct ?? 0).toFixed(1)}%)`);
		if (c.alertEmptyCensus) flags.push('Census empty — no recipients to send to');
		return flags;
	}

	// deliveryTypeID -> commPreference (see CommDeliveryType enum):
	// 1 Always Email+SMS, 2 SMS-when-no-email (Email+SMS), 3 SMS only, 4 Email only.
	private mapCommPref(deliveryTypeID: number): CommPreference {
		if (deliveryTypeID === 3) return 'sms';
		if (deliveryTypeID === 4) return 'email';
		return 'both';
	}

	// ── Charts ────────────────────────────────────────────────────────

	private buildCharts() {
		const groups = this.period === 'month'
			? this.weeklyBuckets(this.activityPoints)
			: this.activityPoints.map(p => [p]);

		if (groups.length < 2) {
			this.emailChart = this.smsChart = null;
			return;
		}

		const labels = groups.map(g => this.period === 'month'
			? this.monthDayLabel(g[0].activityDate)
			: this.weekdayLabel(g[0].activityDate));

		const sum = (g: CommActivityPointDto[], key: keyof CommActivityPointDto) =>
			g.reduce((acc, p) => acc + (p[key] as number), 0);

		const emailSeries: ChartSeries[] = [
			{ label: 'Sent', color: '#072B31', data: groups.map(g => sum(g, 'emailSent')) },
			{ label: 'Delivered', color: '#58a0ed', data: groups.map(g => sum(g, 'emailDelivered')) },
			{ label: 'Opened', color: '#8ac040', data: groups.map(g => sum(g, 'emailOpened')) },
			{ label: 'Clicked', color: '#FDD26E', data: groups.map(g => sum(g, 'emailClicked')) },
			{ label: 'Bounced', color: '#ea780e', data: groups.map(g => sum(g, 'emailBounced')) },
		];
		const smsSeries: ChartSeries[] = [
			{ label: 'Sent', color: '#072B31', data: groups.map(g => sum(g, 'smsSent')) },
			{ label: 'Delivered', color: '#58a0ed', data: groups.map(g => sum(g, 'smsDelivered')) },
			{ label: 'Undelivered', color: '#ea780e', data: groups.map(g => sum(g, 'smsUndelivered')) },
			{ label: 'Failed', color: '#B83B2F', data: groups.map(g => sum(g, 'smsFailed')) },
		];

		this.emailChart = this.buildChart(emailSeries, labels);
		this.smsChart = this.buildChart(smsSeries, labels);
	}

	private weeklyBuckets(points: CommActivityPointDto[]): CommActivityPointDto[][] {
		const buckets: CommActivityPointDto[][] = [];
		for (let i = 0; i < points.length; i += 7) {
			buckets.push(points.slice(i, i + 7));
		}
		return buckets;
	}

	private weekdayLabel(iso: string): string {
		return this.weekdayAbbr[new Date(iso).getDay()];
	}

	private monthDayLabel(iso: string): string {
		const d = new Date(iso);
		return `${this.monthAbbr[d.getMonth()]} ${d.getDate()}`;
	}

	buildChart(series: ChartSeries[], labels: string[]): ChartData {
		const allVals = series.flatMap(s => s.data);
		const maxVal = Math.max(...allVals, 1);
		const niceMax = this.getNiceMax(maxVal);
		const n = labels.length;

		const computedSeries: ChartSeries[] = series.map(s => ({
			...s,
			points: s.data.map((v, i) => `${this.cx(i, n).toFixed(1)},${this.cy(v, niceMax).toFixed(1)}`).join(' '),
			areaPoints: [
				...s.data.map((v, i) => `${this.cx(i, n).toFixed(1)},${this.cy(v, niceMax).toFixed(1)}`),
				`${this.cx(n - 1, n).toFixed(1)},165`,
				`${this.cx(0, n).toFixed(1)},165`
			].join(' '),
			dots: s.data.map((v, i) => ({ x: this.cx(i, n), y: this.cy(v, niceMax) }))
		}));

		return {
			series: computedSeries,
			gridLines: this.getGridLines(niceMax),
			xLabels: labels.map((label, i) => ({ label, x: this.cx(i, n) }))
		};
	}

	private cx(i: number, n: number): number { return 55 + i * (415 / (n - 1)); }
	private cy(val: number, max: number): number { return 165 - (val / max) * 155; }

	private getNiceMax(maxVal: number): number {
		if (maxVal <= 0) return 10;
		const exp = Math.floor(Math.log10(maxVal));
		const factor = Math.pow(10, exp);
		const norm = maxVal / factor;
		if (norm <= 1) return factor;
		if (norm <= 2) return 2 * factor;
		if (norm <= 5) return 5 * factor;
		return 10 * factor;
	}

	private getGridLines(niceMax: number): { y: number; label: string }[] {
		const step = niceMax / 4;
		return [0, 1, 2, 3, 4].map(i => {
			const val = i * step;
			const y = parseFloat(this.cy(val, niceMax).toFixed(1));
			const label = val >= 1000 ? `${(val / 1000).toFixed(val >= 10000 ? 0 : 1)}k` : `${Math.round(val)}`;
			return { y, label };
		});
	}

	// ── Campaign table ────────────────────────────────────────────────

	sortCampaigns(column: string, toggle: boolean = true) {
		if (toggle) {
			this.sortDirection = this.sortColumn === column
				? (this.sortDirection === 'asc' ? 'desc' : 'asc')
				: 'asc';
		}
		this.sortColumn = column;

		this.campaigns.sort((a, b) => {
			let va: any = (a as any)[column];
			let vb: any = (b as any)[column];
			if (va === null || va === undefined) va = this.sortDirection === 'asc' ? Infinity : -Infinity;
			if (vb === null || vb === undefined) vb = this.sortDirection === 'asc' ? Infinity : -Infinity;
			if (va instanceof Date && vb instanceof Date) {
				va = va.getTime(); vb = vb.getTime();
			} else if (typeof va === 'string') {
				va = va.toLowerCase(); vb = (vb as string).toLowerCase();
			}
			if (va < vb) return this.sortDirection === 'asc' ? -1 : 1;
			if (va > vb) return this.sortDirection === 'asc' ? 1 : -1;
			return 0;
		});
	}

	getSortIcon(col: string): string {
		if (this.sortColumn !== col) return 'sort';
		return this.sortDirection === 'asc' ? 'sort-up' : 'sort-down';
	}

	getCampaignStatus(c: CampaignRow): 'ok' | 'warning' {
		return c.flags.length > 0 ? 'warning' : 'ok';
	}

	// Icon for the unsent-row tag in the Send Date column (scheduled/overdue/paused).
	scheduleIcon(status: ScheduleStatus): string {
		if (status === 'overdue') return 'event_busy';
		if (status === 'paused') return 'pause_circle';
		return 'schedule';
	}

	getStage(): EmployerStage {
		// Stage is computed in upCommDashboardEmployers (single source of truth); the client only displays it.
		return this.employerDetail.lifecycle.stage;
	}

	getStageLabel(stage: EmployerStage): string {
		return stage.charAt(0).toUpperCase() + stage.slice(1);
	}

	getCommPrefIcon(pref: CommPreference): string {
		if (pref === 'email') return 'mail';
		if (pref === 'sms') return 'sms';
		return 'forum';
	}

	getCommPrefLabel(pref: CommPreference): string {
		if (pref === 'email') return 'Email';
		if (pref === 'sms') return 'SMS';
		return 'Email + SMS';
	}
}
